import OnboardingFlow from '@/components/onboarding/OnboardingFlow';
import { supabase } from '@/lib/supabase';
import { router, useLocalSearchParams } from 'expo-router';
import { useEffect, useState } from 'react';
import { ActivityIndicator, StyleSheet, View } from 'react-native';
import { Text } from 'react-native-paper';

// Design System Colors
const colors = {
  indigo600: '#6366F1',
  slate500: '#64748B',
  slate50: '#F8FAFC',
};

interface ExistingData {
  fullName: string;
  diploma: string;
  section: string;
  subjectCodes: string[];
}

export default function OnboardingScreen() {
  const { edit, changeDiploma } = useLocalSearchParams<{ edit?: string; changeDiploma?: string }>();
  const isEditMode = edit === 'true';
  const isChangeDiplomaMode = changeDiploma === 'true';

  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState<string | null>(null);
  const [existingData, setExistingData] = useState<ExistingData | null>(null);
  const [debugInfo, setDebugInfo] = useState('Initialisation...');

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      console.log('=== ONBOARDING: Checking auth ===');
      console.log('=== Edit mode:', isEditMode);
      console.log('=== Change diploma mode:', isChangeDiplomaMode);
      setDebugInfo('Vérification authentification...');

      const { data: { user }, error: authError } = await supabase.auth.getUser();
      
      console.log('=== User:', user?.id || 'No user');
      console.log('=== Auth Error:', authError);

      if (authError) {
        console.log('=== Auth error, redirecting to login ===');
        setDebugInfo(`Erreur auth: ${authError.message}`);
        router.replace('/login');
        return;
      }

      if (!user) {
        console.log('=== No user, redirecting to login ===');
        setDebugInfo('Pas d\'utilisateur, redirection login...');
        router.replace('/login');
        return;
      }

      setDebugInfo(`Utilisateur trouvé: ${user.id.substring(0, 8)}...`);
      console.log('=== Checking profile ===');

      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('onboarding_completed, full_name, diploma, section, subject_codes')
        .eq('id', user.id)
        .maybeSingle();

      console.log('=== Profile:', profile);
      console.log('=== Profile Error:', profileError);

      if (profileError) {
        console.log('=== Profile error:', profileError.message);
        setDebugInfo(`Erreur profil: ${profileError.message}`);
      }

      // Mode édition OU mode changement diplôme : charger les données existantes
      if ((isEditMode || isChangeDiplomaMode) && profile) {
        console.log('=== Edit/ChangeDiploma mode: loading existing data ===');
        setExistingData({
          fullName: profile.full_name || '',
          diploma: profile.diploma || '',
          section: profile.section || '',
          subjectCodes: profile.subject_codes || [],
        });
        setUserId(user.id);
        setLoading(false);
        return;
      }

      // Mode normal : vérifier si onboarding déjà fait
      if (profile?.onboarding_completed === true && !isEditMode && !isChangeDiplomaMode) {
        console.log('=== Onboarding already done, going to tabs ===');
        setDebugInfo('Onboarding déjà fait, redirection...');
        router.replace('/(tabs)');
        return;
      }

      console.log('=== Starting onboarding ===');
      setDebugInfo('Démarrage onboarding...');
      setUserId(user.id);
      setLoading(false);
    } catch (error: any) {
      console.error('=== ONBOARDING ERROR ===', error);
      setDebugInfo(`Erreur: ${error.message}`);
    }
  };

  const handleComplete = () => {
    if (isEditMode || isChangeDiplomaMode) {
      // En mode édition ou changement diplôme, retourner au profil
      router.replace('/(tabs)/profile');
    } else {
      // En mode normal, aller au dashboard
      router.replace('/(tabs)');
    }
  };

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={colors.indigo600} />
        <Text style={styles.debugText}>{debugInfo}</Text>
      </View>
    );
  }

  if (!userId) {
    return (
      <View style={styles.loadingContainer}>
        <Text style={styles.debugText}>Erreur: userId manquant</Text>
      </View>
    );
  }

  return (
    <OnboardingFlow 
      userId={userId} 
      onComplete={handleComplete}
      editMode={isEditMode}
      existingData={existingData}
      // ✅ En mode changeDiploma, on saute à l'étape 2 (choix diplôme)
      skipToStep={isChangeDiplomaMode ? 3 : undefined}
    />
  );
}

const styles = StyleSheet.create({
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: colors.slate50,
  },
  debugText: {
    marginTop: 20,
    color: colors.slate500,
    fontSize: 14,
    textAlign: 'center',
    paddingHorizontal: 20,
  },
});
